import { Field as BaseField } from '@base-ui/react/field'
import clsx from 'clsx'
import type { ComponentPropsWithoutRef } from 'react'

// Field Root Component
interface FieldRootProps extends ComponentPropsWithoutRef<typeof BaseField.Root> {}

function FieldRoot({ className, ...props }: FieldRootProps) {
  return <BaseField.Root className={clsx('flex flex-col gap-2', className)} {...props} />
}

// Field Label Component
interface FieldLabelProps extends ComponentPropsWithoutRef<typeof BaseField.Label> {}

function FieldLabel({ className, children, ...props }: FieldLabelProps) {
  return (
    <BaseField.Label
      className={clsx(
        // Rails styling + current React styling
        'text-charcoal-75 text-base font-bold font-inter',
        // Required field styling (if needed later)
        'data-required:after:content-["*"] data-required:after:text-brick data-required:after:ml-1',
        className
      )}
      {...props}
    >
      {children}
    </BaseField.Label>
  )
}

// Field Description Component
interface FieldDescriptionProps extends ComponentPropsWithoutRef<typeof BaseField.Description> {}

function FieldDescription({ className, children, ...props }: FieldDescriptionProps) {
  return (
    <BaseField.Description
      className={clsx(
        // Rails hint styling
        'text-charcoal-50 text-sm font-medium',
        className
      )}
      {...props}
    >
      {children}
    </BaseField.Description>
  )
}

// Field Error Component
interface FieldErrorProps extends ComponentPropsWithoutRef<typeof BaseField.Error> {}

function FieldError({ className, children, ...props }: FieldErrorProps) {
  return (
    <BaseField.Error
      className={clsx(
        // Error styling - inline below input
        'text-brick-light text-sm font-medium',
        className
      )}
      {...props}
    >
      {children}
    </BaseField.Error>
  )
}

// Export as compound component
export const Field = {
  Root: FieldRoot,
  Label: FieldLabel,
  Description: FieldDescription,
  // Field Control Component - used to adapt a third party component as a field
  Control: BaseField.Control,
  Error: FieldError,
}
